import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PoblacionRepository } from '../../../domain/service/transporte/poblacion-repository';
import { Poblacion, PoblacionFiltro, PoblacionSearchResponse } from '../../../domain/model/transporte/poblacion';
import { environment } from '../../../../environments/environment';
import { validarSize } from 'src/app/common/validation-utils';

/**
 * Implementación HTTP del repositorio de Población/Tarifas de Transporte.
 *
 * Consume el endpoint de servicios-ms para consultar W094_SGS_TARIFAS_TRANSPORTE.
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable({ providedIn: 'root' })
export class HttpPoblacionRepository extends PoblacionRepository {

  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/transporte`;

  constructor(private readonly http: HttpClient) {
    super();
  }

  /**
   * Busca poblaciones disponibles según municipios de origen y destino, con
   * paginación keyset/cursor (skill acceso-datos-oracle §7 — nunca OFFSET).
   *
   * GET /api/servicios/transporte/poblaciones
   *
   * Legacy: ORDER BY campo2 (dspoblacion) - ordena por nombre.
   *
   * @param filtro Filtros de búsqueda (requiere CDSURA de ambos municipios)
   * @param cursor Cursor de la página (null = primera página)
   * @param size Tamaño de página
   * @param direction 'NEXT' | 'PREVIOUS' | 'LAST'
   */
  buscarPoblaciones(
    filtro: PoblacionFiltro,
    cursor: string | null,
    size = 20,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST' = 'NEXT'
  ): Observable<PoblacionSearchResponse> {
    validarSize(size);
    let params = new HttpParams()
      .set('cdsuraOrigen', filtro.cdsuraOrigen)
      .set('cdsuraDestino', filtro.cdsuraDestino)
      .set('size', size.toString())
      .set('direction', direction);

    // Filtro por código (campo1 en legacy)
    if (filtro.cdpoblacion) {
      params = params.set('cdpoblacion', filtro.cdpoblacion);
    }

    // Filtro por nombre (campo2 en legacy)
    if (filtro.dspoblacion) {
      params = params.set('dspoblacion', filtro.dspoblacion);
    }

    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http.get<PoblacionSearchResponse>(
      `${this.baseUrl}/poblaciones`,
      { params }
    ).pipe(
      catchError(error => {
        console.error('Error buscando poblaciones:', error);
        return of({
          content: [],
          totalElements: 0,
          hasNext: false,
          hasPrevious: false
        });
      })
    );
  }

  /**
   * Obtiene una tarifa de transporte específica.
   *
   * GET /api/servicios/transporte/tarifa
   */
  obtenerTarifa(
    cdsuraOrigen: string,
    cdsuraDestino: string,
    cdpoblacion?: string,
    nmconsecutivoOficina?: number,
    nmconsecutivoCliente?: number,
    nmconsecutivoAsesor?: number
  ): Observable<Poblacion | null> {
    let params = new HttpParams()
      .set('cdsuraOrigen', cdsuraOrigen)
      .set('cdsuraDestino', cdsuraDestino);

    // Solo enviar cdpoblacion cuando realmente tenga valor.
    if (cdpoblacion && cdpoblacion !== 'undefined') {
      params = params.set('cdpoblacion', cdpoblacion);
    }

    // Legacy fallback: si no existe tarifa en W094, backend consulta perímetro por oficina/cliente.
    if (nmconsecutivoOficina !== undefined && nmconsecutivoOficina !== null) {
      params = params.set('nmconsecutivoOficina', String(nmconsecutivoOficina));
    }
    if (nmconsecutivoCliente !== undefined && nmconsecutivoCliente !== null) {
      params = params.set('nmconsecutivoCliente', String(nmconsecutivoCliente));
    }

    // Citas complementarias (sin orden): el backend resuelve oficina y cliente
    // a partir del asesor de la cita.
    // Legacy: CrearCitaAdministrativaActivity.fulFillment().
    if (nmconsecutivoAsesor !== undefined && nmconsecutivoAsesor !== null) {
      params = params.set('nmconsecutivoAsesor', String(nmconsecutivoAsesor));
    }

    return this.http.get<Poblacion>(
      `${this.baseUrl}/tarifa`,
      { params }
    ).pipe(
      catchError(error => {
        console.error('Error obteniendo tarifa:', error);
        return of(null);
      })
    );
  }
}
